import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { Dropdown, InlineNotification } from '@carbon/react';
import {
  EAutoRange,
  EAxisAlignment,
  NumberRange,
  NumericAxis,
  Thickness,
} from 'scichart';
import {
  SciChartView,
  type SciChartInitContext,
} from 'components/charts/scichart';
import { formatNumber } from 'utils/number';
import { mapScopeAtom, serverFiltersAtom } from '../atoms';
import {
  useRegisterChartExport,
  type ChartExportDescriptor,
} from '../chart-export-registry';
import { useEval3Page } from '../context';
import {
  useCalcLifecycle,
  useChartFieldOptions,
  useChartSeries,
  usePartialTracts,
} from '../hooks';
import { sampledNote } from './util';
import { ChartLegend } from './chart-legend';
import {
  buildLegend,
  renderChartFamily,
  withAssetLabels,
} from './spec-chart-render';
import { ChartStatusOverlay } from './chart-status';
import type {
  ChartAggregate,
  ChartFieldKind,
  ChartFieldOption,
  ChartPlane,
  ChartSpec,
  ChartStyle,
  ChartStyleByX,
} from './types';
import styles from './custom-chart.module.scss';

// Axis text sizes — match the well-page chart tokens (see production-volumes-chart).
const AXIS_LABEL_STYLE = { fontSize: 11, padding: new Thickness(0, 6, 0, 0) };
const AXIS_TITLE_STYLE = { fontSize: 12 };

// ── Field vocabulary (server-driven) ────────────────────────────────────────
// The plottable fields, styles, aggregates, and the style↔x-type rules all come from
// `useChartFieldOptions` (POST /queries/packages/chart_field_options), served from the
// authoritative backend `chart_series` FIELDS registry — so the builder never hardcodes a
// parallel field list or a parallel styleByX map. Only the render-side bits the endpoint
// can't own stay FE-local: the `format` → axis-formatter mapping and the style display labels.

// Compact "$1,235k" for the dollar (`currency`) metrics.
const dollarsK = (value: number): string => {
  if (value === 0) {
    return '$0';
  }
  return `$${formatNumber({
    value: Math.round(value / 1000),
    minPrecision: 0,
    maxPrecision: 0,
    includeCommas: true,
    fallback: '0',
  })}k`;
};

// Plain integer with commas (volumes / acres — the `decimal` / `integer` metrics).
const wholeNumber = (value: number): string =>
  formatNumber({
    value,
    minPrecision: 0,
    maxPrecision: 0,
    includeCommas: true,
    fallback: '0',
  });

// Backend `format` hint → axis number formatter. `text` / `date` never reach a numeric axis.
const FORMATTERS: Record<string, (value: number) => string> = {
  currency: dollarsK,
  decimal: wholeNumber,
  integer: wholeNumber,
};

// The axis formatter for a field, falling back to a rounded integer.
const axisFormatter = (
  field: ChartFieldOption | undefined
): ((value: number) => string) =>
  (field && FORMATTERS[field.format]) ?? ((value) => `${Math.round(value)}`);

/** Display labels for the aggregate enum (the endpoint returns the bare keys). */
const AGGREGATE_LABELS: Record<ChartAggregate, string> = {
  mean: 'Mean',
  sum: 'Sum',
  count: 'Count',
};

/** Display labels for the style enum (the endpoint returns the bare keys). */
const STYLE_LABELS: Record<ChartStyle, string> = {
  scatter: 'Scatter Plot',
  bar: 'Bar Chart',
  line: 'Line Chart',
  stackedBar: 'Stacked Bar',
};

// Fallbacks used only until `chart_field_options` loads, so the dropdowns aren't empty on the
// first paint. Mirror the backend defaults; replaced by the server values as soon as they land.
const DEFAULT_STYLES: ChartStyle[] = ['scatter', 'bar', 'line', 'stackedBar'];
const DEFAULT_STYLE_BY_X: ChartStyleByX = {
  temporal: ['line', 'bar'],
  categorical: ['bar', 'stackedBar', 'line'],
  numeric: ['scatter'],
};

/**
 * The plane a chart lives on, derived from the chosen X field — the narrowing key that
 * replaced `dataSource`. A temporal `month` X is served by the monthly path; every other
 * X delegates to ValueRollup (the slot plane). A series is offered only when its `planes`
 * include this.
 *
 * @param {ChartFieldOption | undefined} x - The selected X field's meta.
 * @return {ChartPlane} The plane the chart lives on.
 */
const planeForX = (x: ChartFieldOption | undefined): ChartPlane => {
  if (x?.type === 'temporal' && x.planes.includes('monthly')) {
    return 'monthly';
  }
  return 'slot';
};

// Sentinel for the "no Color By" Dropdown item — Carbon needs a concrete item, and `''`
// never collides with a real field key. Maps to omitting `spec.colorBy`.
const NO_COLOR_BY = '';

/**
 * Builder chart type — 1:1 with the render `ChartStyle`s: scatter (numeric X, points), bar
 * (categorical/temporal X, aggregated columns), line (temporal or categorical X, aggregated
 * curve), stackedBar (categorical X, segments by colorBy). Bar/line/stackedBar send an
 * aggregate; scatter doesn't.
 */
type BuilderChartType = ChartStyle;

/**
 * Whether a chart type aggregates rows per X-group (so it must send an aggregate). Bar/line/
 * stackedBar collapse many rows into one value; scatter plots raw points, so it never sends one.
 *
 * @param {BuilderChartType} type - The chart type.
 * @return {boolean} True for bar/line/stackedBar.
 */
const needsAggregate = (type: BuilderChartType): boolean =>
  type === 'bar' || type === 'line' || type === 'stackedBar';

/**
 * Whether a chart type supports Color By. The backend accepts a colorBy only on scatter (it
 * fans one metric into a point series per category) and stackedBar (the stack segments); it
 * rejects it on plain bar and line. So the builder offers it for exactly those two.
 *
 * @param {BuilderChartType} type - The chart type.
 * @return {boolean} True for scatter/stackedBar.
 */
const supportsColorBy = (type: BuilderChartType): boolean =>
  type === 'scatter' || type === 'stackedBar';

/**
 * The x-field TYPES a style admits, read straight from the server `styleByX` map (inverted:
 * a type admits the style iff its list includes it). This replaces the old hardcoded rules,
 * so e.g. a categorical-X line chart is offered because `styleByX.categorical` lists `line`.
 *
 * @param {ChartStyleByX} styleByX - The server style↔x-type map.
 * @param {BuilderChartType} style - The chart type.
 * @return {ChartFieldKind[]} The x-field types valid for the style.
 */
const xTypesForStyle = (
  styleByX: ChartStyleByX,
  style: BuilderChartType
): ChartFieldKind[] =>
  (['temporal', 'categorical', 'numeric'] as const).filter((type) =>
    (styleByX[type] ?? []).includes(style)
  );

/**
 * The X fields valid for a chart type, from the ungated set: the fields whose `type` the
 * style admits (per `styleByX`) and that can be an X (`roles` includes `x`). A stackedBar
 * additionally requires a group-by categorical (`groupBy !== false`) — it stacks a metric by
 * that dimension.
 *
 * @param {ChartFieldOption[]} available - Ungated, plottable fields.
 * @param {BuilderChartType} style - The chart type.
 * @param {ChartStyleByX} styleByX - The server style↔x-type map.
 * @return {ChartFieldOption[]} The X fields valid for the type.
 */
const xFieldsForType = (
  available: ChartFieldOption[],
  style: BuilderChartType,
  styleByX: ChartStyleByX
): ChartFieldOption[] => {
  const types = xTypesForStyle(styleByX, style);
  return available.filter(
    (f) =>
      types.includes(f.type) &&
      f.roles.includes('x') &&
      (style !== 'stackedBar' || f.groupBy !== false)
  );
};

/**
 * Custom Chart body — a configurable X/Y plot backed by `chart_series`: pick the chart type,
 * the axis fields, the aggregate, and (scatter/stackedBar) a Color By. The dropdown
 * vocabulary — fields, styles, and the style↔x-type rules — is fetched from
 * `chart_field_options` (calc-scoped), so the builder never hardcodes a field list. Body
 * only: the card chrome is owned by `ChartCard`. Assembles a `ChartSpec` live and fetches
 * through `useChartSeries`, drawing via the shared `renderChartFamily` (so stacking / legend
 * match the catalog charts). A `422` spec error surfaces on the control that owns it (or an
 * inline banner); loading / empty / sampled states layer over the surface.
 *
 * @param {object} props - The persisted `initialSpec` (seeds the builder once) and the `onSpecChange` write-back.
 * @return {JSX.Element} The chart body.
 */
export function CustomChart({
  initialSpec,
  onSpecChange,
  registerExport,
}: {
  /** The instance's persisted authored spec — seeds the builder controls on mount (once). */
  initialSpec?: ChartSpec;
  /** Persist the builder's edited (filter-free) spec back to the instance. */
  onSpecChange?: (spec: ChartSpec) => void;
  /** When set, this chart's SciChart surface registers for content export. */
  registerExport?: ChartExportDescriptor;
}) {
  const { calculationId } = useEval3Page();
  const filters = useAtomValue(serverFiltersAtom);
  const mapScope = useAtomValue(mapScopeAtom);
  const { isRunning } = useCalcLifecycle(calculationId);
  // Registers/unregisters this chart's image exporter as its surface mounts/unmounts;
  // a no-op when `registerExport` is absent (chart isn't exportable).
  const onExporterReady = useRegisterChartExport(registerExport);
  // The legend DOM node — passed to the export so it's stacked above the captured chart image
  // (the legend is a sibling of the SciChart canvas, not part of the captured surface).
  const legendRef = useRef<HTMLDivElement>(null);

  // Seed the controls from the persisted spec ONCE (lazy initializers). A brand-new custom
  // chart seeds from the catalog `custom` default; a rehydrated one from its saved spec. After
  // mount the controls own the state — a later `initialSpec` prop change is ignored (no loop
  // with the onSpecChange write-back). Falls back to the prior hardcoded defaults when absent.
  const seedSeries = initialSpec?.series?.[0];
  const [chartType, setChartType] = useState<BuilderChartType>(
    seedSeries?.style ?? 'scatter'
  );
  const [xField, setXField] = useState<string>(
    initialSpec?.x?.field ?? 'grossOil'
  );
  const [yField, setYField] = useState<string>(
    seedSeries?.field ?? 'targetPrice'
  );
  // Bar/line/stackedBar collapse many rows per X-group into one value, so they pick an
  // aggregate; scatter ignores it. Reconciled to the Y field's kind below.
  const [aggregate, setAggregate] = useState<ChartAggregate>(
    seedSeries?.aggregate ?? 'mean'
  );
  // Optional Color By — a categorical field that fans one metric into one series per
  // category (a stackedBar's stack segments). `NO_COLOR_BY` (default) omits `spec.colorBy`.
  const [colorBy, setColorBy] = useState<string>(
    initialSpec?.colorBy ?? NO_COLOR_BY
  );

  // The builder vocabulary (the data-driven axis map) — scoped by `calculationId` alone (the
  // contract carries no `packageId`) and gated on a calc existing, so the dropdowns populate
  // in calc mode and stay empty until then.
  const { data: fieldOptions } = useChartFieldOptions({ calculationId });
  // `fieldOptions` is a stable React Query reference, so `fieldOptions.fields` is already
  // stable for the `[fields]` memos below — no `useMemo` wrapper needed (only the `[]`
  // loading fallback churns, when everything downstream is empty anyway).
  const fields = fieldOptions?.fields ?? [];
  // Ungated, plottable fields — the deferred/gated ones (rate/price series, slot temporal
  // axes, `irr`) are hidden; chart_series 422s on them. Keyed off the stable `fieldOptions`
  // (not the `fields` fallback expression) so these memos — which feed the `[ungatedFields]`
  // deps below — keep a stable identity without `fields` needing its own `useMemo`.
  const ungatedFields = useMemo(
    () => (fieldOptions?.fields ?? []).filter((f) => !f.gated),
    [fieldOptions]
  );

  // Field lookup for axis titles / formatters.
  const fieldByKey = useMemo(
    () => new Map((fieldOptions?.fields ?? []).map((f) => [f.key, f])),
    [fieldOptions]
  );

  // Field key → human label for the legend (so a single-metric series reads "Net Cash Flow"
  // rather than the raw `netCashflow` name), shared with SpecChart via `buildLegend`.
  const labelByField = useMemo(
    () => new Map((fieldOptions?.fields ?? []).map((f) => [f.key, f.label])),
    [fieldOptions]
  );

  // The style vocabulary + the style↔x-type rules, both server-driven (fallbacks only until
  // the vocabulary loads).
  const styleOptionKeys = fieldOptions?.styles ?? DEFAULT_STYLES;
  const styleByX = fieldOptions?.styleByX ?? DEFAULT_STYLE_BY_X;

  // X options follow the chart type via the server `styleByX` map (scatter → numeric, line →
  // temporal OR categorical, bar → categorical/temporal, stackedBar → group-by categorical).
  // The chosen X fixes the PLANE.
  const xFields = useMemo(
    () => xFieldsForType(ungatedFields, chartType, styleByX),
    [ungatedFields, chartType, styleByX]
  );
  const xMeta = fieldByKey.get(xField);
  // The plane the chart lives on, derived from X — the narrowing key (replaced dataSource).
  const chartPlane: ChartPlane = planeForX(xMeta);

  // Y (series) options: numeric `y`-role fields whose planes include the chart's plane —
  // this is the X↔series narrowing (a monthly-only revenue can't back a slot bar, etc.).
  const yFields = useMemo(
    () =>
      ungatedFields.filter(
        (f) =>
          f.type === 'numeric' &&
          f.roles.includes('y') &&
          f.planes.includes(chartPlane)
      ),
    [ungatedFields, chartPlane]
  );
  // Color By: categorical `colorBy`-role fields on the plane. A stackedBar stacks by a
  // group-by dimension, so it drops colorBy-only fields (`groupBy:false`, e.g. holeDirection).
  const colorByFields = useMemo(
    () =>
      ungatedFields.filter(
        (f) =>
          f.type === 'categorical' &&
          f.roles.includes('colorBy') &&
          f.planes.includes(chartPlane) &&
          (chartType !== 'stackedBar' || f.groupBy !== false)
      ),
    [ungatedFields, chartPlane, chartType]
  );

  const yMeta = fieldByKey.get(yField);
  // Aggregate options come from the selected Y field's KIND (flow/dollar → sum·mean,
  // rate → mean-only, count → count) — so the builder can't offer summing a rate.
  const aggregateChoices = useMemo<ChartAggregate[]>(
    () => yMeta?.aggregates ?? fieldOptions?.aggregates ?? ['mean', 'sum'],
    [yMeta, fieldOptions]
  );

  const toOption = (f: ChartFieldOption) => ({ id: f.key, label: f.label });
  const chartTypeOptions = useMemo(
    () => styleOptionKeys.map((s) => ({ id: s, label: STYLE_LABELS[s] ?? s })),
    [styleOptionKeys]
  );
  const xOptions = useMemo(() => xFields.map(toOption), [xFields]);
  const yOptions = useMemo(() => yFields.map(toOption), [yFields]);
  const aggregateOptions = useMemo(
    () =>
      aggregateChoices.map((a) => ({ id: a, label: AGGREGATE_LABELS[a] ?? a })),
    [aggregateChoices]
  );
  // Color By leads with an explicit "None" (omits `spec.colorBy`), then the categoricals.
  const colorByOptions = useMemo(
    () => [{ id: NO_COLOR_BY, label: 'None' }, ...colorByFields.map(toOption)],
    [colorByFields]
  );

  // Once the vocabulary/plane changes, snap any now-invalid selection to the first valid
  // option — so the spec is always buildable and we never fire a guaranteed-422 request.
  useEffect(() => {
    if (xFields.length > 0 && !xFields.some((f) => f.key === xField)) {
      setXField(xFields[0].key);
    }
  }, [xFields, xField]);
  useEffect(() => {
    if (yFields.length > 0 && !yFields.some((f) => f.key === yField)) {
      setYField(yFields[0].key);
    }
  }, [yFields, yField]);
  // Keep the aggregate within the Y field's allowed set (snap to its kind default otherwise).
  useEffect(() => {
    if (aggregateChoices.length > 0 && !aggregateChoices.includes(aggregate)) {
      setAggregate(yMeta?.defaultAggregate ?? aggregateChoices[0]);
    }
  }, [aggregateChoices, aggregate, yMeta]);
  // A stackedBar REQUIRES a group-by colorBy — seed the first valid one; other types drop
  // a colorBy that's no longer valid for the plane.
  useEffect(() => {
    if (chartType === 'stackedBar') {
      if (
        colorByFields.length > 0 &&
        !colorByFields.some((f) => f.key === colorBy)
      ) {
        setColorBy(colorByFields[0].key);
      }
    } else if (
      colorBy !== NO_COLOR_BY &&
      !colorByFields.some((f) => f.key === colorBy)
    ) {
      setColorBy(NO_COLOR_BY);
    }
  }, [colorByFields, colorBy, chartType]);

  // Switching chart type flips the valid X kind; eagerly reset X to a valid field for the
  // new type (the reconcile effect is the safety net). The plane then follows the new X.
  const onChartType = (next: BuilderChartType) => {
    setChartType(next);
    const nextX = xFieldsForType(ungatedFields, next, styleByX);
    if (nextX.length > 0 && !nextX.some((f) => f.key === xField)) {
      setXField(nextX[0].key);
    }
  };

  const withAggregate = needsAggregate(chartType);
  const withColorBy = supportsColorBy(chartType);

  // The FILTER-FREE authored spec — what gets persisted. Page filters are NOT baked in; they
  // ride live on `spec` (below) at fetch time only, so a saved chart always reflects the
  // CURRENT page filters, not whatever was active when it was saved.
  const authoredSpec = useMemo<ChartSpec>(() => {
    const style: ChartStyle = chartType;
    return {
      // No dataSource — the BE derives the backend from `x`'s type.
      x: { field: xField },
      series: [
        {
          field: yField,
          style,
          ...(needsAggregate(style) ? { aggregate } : {}),
        },
      ],
      // colorBy rides for the styles whose renderer draws per-series entries (and is
      // required for stackedBar — its stack segments).
      ...(supportsColorBy(style) && colorBy ? { colorBy } : {}),
    };
  }, [chartType, xField, yField, aggregate, colorBy]);

  // The fetch spec = the authored spec + the LIVE page filters (merged only here, at request
  // time). Drives `useChartSeries`; matches the table for the same filters.
  const spec = useMemo<ChartSpec>(
    () => (filters.length ? { ...authoredSpec, filters } : authoredSpec),
    [authoredSpec, filters]
  );

  // Persist the authored spec back to the instance whenever a builder selection changes (the
  // chart-config autosave debounces the actual save). Skip the initial run — the seed spec is
  // already what's persisted (a fresh custom chart was saved with the catalog default on add),
  // so re-emitting it would be a redundant no-op. A `latest ref` keeps the callback identity
  // out of the deps, so only a real `authoredSpec` change fires it.
  const onSpecChangeRef = useRef(onSpecChange);
  onSpecChangeRef.current = onSpecChange;
  const emittedOnce = useRef(false);
  useEffect(() => {
    if (!emittedOnce.current) {
      emittedOnce.current = true;
      return;
    }
    onSpecChangeRef.current?.(authoredSpec);
  }, [authoredSpec]);

  // Gate the fetch on the selection being valid for the loaded vocabulary — before it
  // loads the seeded defaults are known-good, so allow the fetch then.
  const selectionValid =
    fields.length === 0 ||
    (xFields.some((f) => f.key === xField) &&
      yFields.some((f) => f.key === yField));

  const { data, isFetching, error, refetch } = useChartSeries(
    { calculationId, spec, mapScope },
    { enabled: selectionValid }
  );

  const note = sampledNote(data?.meta);
  const hasData = (data?.series.length ?? 0) > 0;
  // An Asset (on X or Color By) that names a Tract the package holds in part says how much of
  // it the package holds, on the axis and in the legend alike.
  const { nameForAsset } = usePartialTracts();
  const seriesData = useMemo(
    () => withAssetLabels(data?.series ?? [], spec, nameForAsset),
    [data, spec, nameForAsset]
  );
  // A legend for multi-series output (scatter/line colorBy fan-out, stackedBar segments).
  const legend = useMemo(
    () => buildLegend(seriesData, labelByField),
    [seriesData, labelByField]
  );

  // A 422 spec error carries a `field` path (e.g. `spec.x.field`, `spec.series[0].aggregate`,
  // `spec.colorBy`) — map it to the control that owns it so that dropdown highlights. The
  // series index is always 0 here (single-series builder), so any `spec.series[i].*` matches.
  const errField = error?.field ?? null;
  const xInvalid = errField === 'spec.x.field';
  const yInvalid = /^spec\.series\[\d+\]\.field$/.test(errField ?? '');
  const aggregateInvalid = /^spec\.series\[\d+\]\.aggregate$/.test(
    errField ?? ''
  );
  const colorByInvalid = errField === 'spec.colorBy';
  // When the error pins to a control, that control shows it; otherwise the inline banner does.
  const pinnedToControl =
    xInvalid || yInvalid || aggregateInvalid || colorByInvalid;

  const initChart = useCallback(
    ({ surface, wasmContext }: SciChartInitContext) => {
      const xAxis = new NumericAxis(wasmContext, {
        id: 'xAxis',
        axisAlignment: EAxisAlignment.Bottom,
        autoRange: EAutoRange.Always,
        growBy: new NumberRange(0.05, 0.05),
        drawMinorGridLines: false,
        labelStyle: AXIS_LABEL_STYLE,
        axisTitleStyle: AXIS_TITLE_STYLE,
      });
      surface.xAxes.add(xAxis);

      const yAxis = new NumericAxis(wasmContext, {
        id: 'yAxis',
        axisAlignment: EAxisAlignment.Left,
        autoRange: EAutoRange.Always,
        growBy: new NumberRange(0, 0.1),
        drawMinorGridLines: false,
        labelStyle: AXIS_LABEL_STYLE,
        axisTitleStyle: AXIS_TITLE_STYLE,
      });
      surface.yAxes.add(yAxis);
    },
    []
  );

  // Draw through the shared renderer (same code the catalog charts use) — so stackedBar
  // really stacks and colorBy fans out with a legend. The field-derived axis titles + the
  // `format`-aware number formatters are the only builder-specific chrome.
  const renderData = useCallback(
    (ctx: SciChartInitContext) => {
      const xFieldMeta = fieldByKey.get(xField);
      const yFieldMeta = fieldByKey.get(yField);
      renderChartFamily(ctx, {
        family: chartType,
        seriesData,
        axis: {
          xTitle: xFieldMeta?.label ?? xField,
          yTitle: yFieldMeta?.label ?? yField,
          xFormat: axisFormatter(xFieldMeta),
          yFormat: axisFormatter(yFieldMeta),
        },
      });
    },
    [seriesData, chartType, xField, yField, fieldByKey]
  );

  return (
    <div className={styles.body}>
      <div className={styles.controls}>
        {/* Control order (mock): X Axis, Y Axis, Color By, Chart Type, then the conditional
            Aggregate. They flow into a 2-column grid, so Color By / Aggregate (both
            conditional) shift the later cells when hidden. */}
        <Dropdown
          id="custom-chart-x-axis"
          titleText="X Axis"
          label="Select field"
          items={xOptions}
          itemToString={(item) => item?.label ?? ''}
          selectedItem={xOptions.find((o) => o.id === xField) ?? null}
          onChange={({ selectedItem }) =>
            selectedItem && setXField(selectedItem.id)
          }
          invalid={xInvalid}
          invalidText={error?.message}
          size="md"
        />
        <Dropdown
          id="custom-chart-y-axis"
          titleText="Y Axis"
          label="Select field"
          items={yOptions}
          itemToString={(item) => item?.label ?? ''}
          selectedItem={yOptions.find((o) => o.id === yField) ?? null}
          onChange={({ selectedItem }) =>
            selectedItem && setYField(selectedItem.id)
          }
          invalid={yInvalid}
          invalidText={error?.message}
          size="md"
        />
        {/* Color By — categorical field that fans one metric into a series per category.
            Offered for scatter/stackedBar (the styles the backend accepts a colorBy on). */}
        {withColorBy && (
          <Dropdown
            id="custom-chart-color-by"
            titleText="Color By"
            label="Select"
            items={colorByOptions}
            itemToString={(item) => item?.label ?? ''}
            selectedItem={
              colorByOptions.find((o) => o.id === colorBy) ?? colorByOptions[0]
            }
            onChange={({ selectedItem }) =>
              selectedItem && setColorBy(selectedItem.id)
            }
            invalid={colorByInvalid}
            invalidText={error?.message}
            size="md"
          />
        )}
        <Dropdown
          id="custom-chart-type"
          titleText="Chart Type"
          label="Select"
          items={chartTypeOptions}
          itemToString={(item) => item?.label ?? ''}
          selectedItem={
            chartTypeOptions.find((o) => o.id === chartType) ?? null
          }
          onChange={({ selectedItem }) =>
            selectedItem && onChartType(selectedItem.id)
          }
          size="md"
        />
        {/* No Data Source control — the backend is derived from the X field's type (a
            temporal `month` X → monthly path; else the ValueRollup slot plane). */}
        {/* Aggregate — bar/line/stackedBar collapse rows per X-group, so an aggregate is
            required; hidden for scatter. Options come from the Y field's kind. */}
        {withAggregate && (
          <Dropdown
            id="custom-chart-aggregate"
            titleText="Aggregate"
            label="Select"
            items={aggregateOptions}
            itemToString={(item) => item?.label ?? ''}
            selectedItem={
              aggregateOptions.find((o) => o.id === aggregate) ?? null
            }
            onChange={({ selectedItem }) =>
              selectedItem && setAggregate(selectedItem.id)
            }
            invalid={aggregateInvalid}
            invalidText={error?.message}
            size="md"
          />
        )}
      </div>

      {/* A 422 spec error is user-fixable (a field/aggregate the BE rejected). When the
          error names a control (via its `field` path) that control highlights it; otherwise
          this banner carries it so it reads as builder validation, not a fetch failure. */}
      {error && !pinnedToControl && (
        <InlineNotification
          className={styles.builderError}
          kind="error"
          lowContrast
          hideCloseButton
          title="Can't build this chart"
          subtitle={error.message}
        />
      )}

      {legend && <ChartLegend ref={legendRef} items={legend} />}

      <div className={styles.chartWrap}>
        <SciChartView
          height="100%"
          initChart={initChart}
          renderData={renderData}
          renderDeps={[seriesData, chartType, xField, yField, fieldByKey]}
          onExporterReady={onExporterReady}
          getExportOverlay={legendRef}
        />
        <ChartStatusOverlay
          isLoading={isFetching || isRunning}
          // The error renders inline above (near the controls); don't double it here.
          error={null}
          isEmpty={!error && !hasData}
          emptyMessage={
            calculationId === null
              ? 'Requires a calculated evaluation.'
              : 'No data for the current selection.'
          }
          note={note}
          onRetry={() => refetch()}
        />
      </div>
    </div>
  );
}
